import { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Button,
  Card,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  TextInput,
  Select,
  Stack,
  Text,
  Pagination,
  LoadingOverlay,
  Switch,
  Grid,
  Alert,
} from '@mantine/core';
import {
  IconUsers,
  IconPlus,
  IconEdit,
  IconTrash,
  IconUserCheck,
  IconUserX,
  IconSearch,
  IconX,
  IconChartBar,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { usersService, User, UserRole, UserRoleLabels, CreateUserRequest, UpdateUserRequest } from '../../services/users-service';
import { modals } from '@mantine/modals';

export function UsersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<string | null>(null);
  
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Query per ottenere gli utenti
  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, pageSize, searchTerm, filterRole, filterActive],
    queryFn: () => usersService.getAll(
      page,
      pageSize,
      searchTerm || undefined,
      filterRole ? parseInt(filterRole) : undefined,
      filterActive ? filterActive === 'true' : undefined
    ),
  });

  // Mutation per creare un utente
  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => usersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Successo',
        message: 'Utente creato con successo',
        color: 'green',
      });
      closeModal();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore',
        message: error.response?.data || 'Errore durante la creazione dell\'utente',
        color: 'red',
      });
    },
  });

  // Mutation per aggiornare un utente
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      usersService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Successo',
        message: 'Utente aggiornato con successo',
        color: 'green',
      });
      closeModal();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore',
        message: error.response?.data || 'Errore durante l\'aggiornamento dell\'utente',
        color: 'red',
      });
    },
  });

  // Mutation per eliminare un utente
  const deleteMutation = useMutation({
    mutationFn: (id: number) => usersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Successo',
        message: 'Utente eliminato con successo',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore',
        message: error.response?.data || 'Errore durante l\'eliminazione dell\'utente',
        color: 'red',
      });
    },
  });

  // Mutation per attivare/disattivare un utente
  const toggleActiveMutation = useMutation({
    mutationFn: (id: number) => usersService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Successo',
        message: 'Stato utente modificato con successo',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore',
        message: error.response?.data || 'Errore durante la modifica dello stato',
        color: 'red',
      });
    },
  });

  // Form per creazione/modifica
  const form = useForm({
    initialValues: {
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      role: UserRole.UfficioStile.toString(), // Stringa per il Select
    },
    validate: {
      username: (value: string) => (value.length < 3 ? 'Username deve avere almeno 3 caratteri' : null),
      email: (value: string) => (/^\S+@\S+$/.test(value) ? null : 'Email non valida'),
      firstName: (value: string) => (value.length < 2 ? 'Nome deve avere almeno 2 caratteri' : null),
      lastName: (value: string) => (value.length < 2 ? 'Cognome deve avere almeno 2 caratteri' : null),
      password: (value: string) =>
        editingUser || value.length >= 6 ? null : 'Password deve avere almeno 6 caratteri',
    },
  });

  const handleCreateUser = () => {
    setEditingUser(null);
    form.reset();
    openModal();
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    form.setValues({
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role.toString(), // Converti il numero in stringa per il Select
    });
    openModal();
  };

  const handleDeleteUser = (user: User) => {
    modals.openConfirmModal({
      title: 'Conferma eliminazione',
      children: (
        <Text>
          Sei sicuro di voler eliminare l'utente <strong>{user.fullName}</strong>?
          <br />
          Questa azione non può essere annullata.
        </Text>
      ),
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate(user.id),
    });
  };

  const handleSubmit = form.onSubmit((values) => {
    // Converti il ruolo da stringa a numero
    const data = {
      ...values,
      role: parseInt(values.role as string),
    };
    
    if (editingUser) {
      updateMutation.mutate({
        id: editingUser.id,
        data: data as UpdateUserRequest,
      });
    } else {
      createMutation.mutate(data as CreateUserRequest);
    }
  });

  const getRoleBadgeColor = (role: UserRole): string => {
    switch (role) {
      case UserRole.Admin:
        return 'red';
      case UserRole.CapoUfficioStile:
        return 'blue';
      case UserRole.UfficioStile:
        return 'cyan';
      case UserRole.Produzione:
        return 'orange';
      case UserRole.Industrializzazione:
        return 'green';
      case UserRole.GestioneOrdini:
        return 'violet';
      case UserRole.ReadOnly:
        return 'gray';
      default:
        return 'gray';
    }
  };

  const totalPages = data ? Math.ceil(data.totalCount / pageSize) : 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconUsers size={32} />
            <Title order={2}>Gestione Utenti</Title>
          </Group>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateUser}>
            Nuovo Utente
          </Button>
        </Group>

        {/* Filtri */}
        <Card withBorder p="md">
          <Grid>
            <Grid.Col span={4}>
              <TextInput
                placeholder="Cerca per nome, email o username..."
                leftSection={<IconSearch size={16} />}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                  setPage(1);
                }}
                rightSection={
                  searchTerm && (
                    <ActionIcon size="xs" onClick={() => setSearchTerm('')}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                placeholder="Filtra per ruolo"
                data={Object.entries(UserRoleLabels).map(([value, label]) => ({
                  value: value,
                  label: label as string,
                }))}
                value={filterRole}
                onChange={(value) => {
                  setFilterRole(value);
                  setPage(1);
                }}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                placeholder="Filtra per stato"
                data={[
                  { value: 'true', label: 'Attivi' },
                  { value: 'false', label: 'Disattivati' },
                ]}
                value={filterActive}
                onChange={(value) => {
                  setFilterActive(value);
                  setPage(1);
                }}
                clearable
              />
            </Grid.Col>
          </Grid>
        </Card>

        {/* Alert informativo */}
        <Alert color="blue" title="Informazione">
          Solo gli utenti con ruolo <strong>Admin</strong> possono gestire gli utenti del sistema.
        </Alert>

        {/* Tabella utenti */}
        <Card withBorder p={0} style={{ position: 'relative' }}>
          <LoadingOverlay visible={isLoading} />
          {error ? (
            <Card.Section p="xl">
              <Text c="red" ta="center">
                Errore durante il caricamento degli utenti
              </Text>
            </Card.Section>
          ) : data && data.items.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nome Utente</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Ruolo</Table.Th>
                  <Table.Th>Stato</Table.Th>
                  <Table.Th>Statistiche</Table.Th>
                  <Table.Th>Azioni</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.items.map((user: User) => (
                  <Table.Tr key={user.id}>
                    <Table.Td>
                      <div>
                        <Text fw={500}>{user.fullName}</Text>
                        <Text size="xs" c="dimmed">
                          {user.username}
                        </Text>
                      </div>
                    </Table.Td>
                    <Table.Td>{user.email}</Table.Td>
                    <Table.Td>
                      <Badge color={getRoleBadgeColor(user.role)} style={{ color: 'white' }}>
                        {UserRoleLabels[user.role]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={user.isActive ? 'green' : 'gray'} style={{ color: 'white' }}>
                        {user.isActive ? 'Attivo' : 'Disattivato'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Task assegnati">
                          <Badge variant="light">{user.assignedTasksCount}</Badge>
                        </Tooltip>
                        <Tooltip label="Task completati">
                          <Badge variant="light" color="green">
                            {user.completedTasksCount}
                          </Badge>
                        </Tooltip>
                        <Tooltip label="Richieste create">
                          <Badge variant="light" color="blue">
                            {user.createdRequestsCount}
                          </Badge>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Modifica">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => handleEditUser(user)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={user.isActive ? 'Disattiva' : 'Attiva'}>
                          <ActionIcon
                            variant="light"
                            color={user.isActive ? 'orange' : 'green'}
                            onClick={() => toggleActiveMutation.mutate(user.id)}
                          >
                            {user.isActive ? <IconUserX size={16} /> : <IconUserCheck size={16} />}
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Elimina">
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Card.Section p="xl">
              <Text ta="center" c="dimmed">
                {searchTerm || filterRole || filterActive
                  ? 'Nessun utente trovato con i filtri selezionati.'
                  : 'Nessun utente presente.'}
              </Text>
            </Card.Section>
          )}
        </Card>

        {/* Paginazione */}
        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
            <Text size="sm" c="dimmed">
              Visualizzati {Math.min((page - 1) * pageSize + 1, data?.totalCount || 0)}-
              {Math.min(page * pageSize, data?.totalCount || 0)} di {data?.totalCount || 0}
            </Text>
          </Group>
        )}
      </Stack>

      {/* Modal Crea/Modifica Utente */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput
              label="Username"
              placeholder="username"
              required
              {...form.getInputProps('username')}
            />
            <TextInput
              label="Email"
              placeholder="email@example.com"
              type="email"
              required
              {...form.getInputProps('email')}
            />
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Nome"
                  placeholder="Mario"
                  required
                  {...form.getInputProps('firstName')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Cognome"
                  placeholder="Rossi"
                  required
                  {...form.getInputProps('lastName')}
                />
              </Grid.Col>
            </Grid>
            {!editingUser && (
              <TextInput
                label="Password"
                placeholder="••••••••"
                type="password"
                required
                {...form.getInputProps('password')}
              />
            )}
            <Select
              label="Ruolo"
              placeholder="Seleziona ruolo"
              data={Object.entries(UserRoleLabels).map(([value, label]) => ({
                value: value,
                label: label as string,
              }))}
              required
              {...form.getInputProps('role')}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeModal}>
                Annulla
              </Button>
              <Button
                type="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingUser ? 'Salva Modifiche' : 'Crea Utente'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
}
